import {useMutation, useQuery} from "@tanstack/react-query"
import {paymentArrangementAPI} from "@indebted/api/paymentArrangementAPI"
import {calculatePaymentArrangementSetup} from "@indebted/api/paymentArrangementCalculator"
import {useNavigate} from "react-router-dom"
import {useNotification} from "@indebted/components/Notification"
import {useTranslation} from "react-i18next"
import {format} from "date-fns"
import {getSetupPage} from "@indebted/api/paymentArrangement/setup"

import {nextPage} from "../Navigator"

import {Frequency} from "./Frequency"

export const useSetupQueryOrCalculate = ({
	paymentArrangementID,
	frequency,
	repayments,
	startDate,
	configuration,
	enabled,
	sliderValue,
}: {
	paymentArrangementID: string
	frequency: Frequency
	repayments: number
	startDate: Date
	configuration?: {
		ClientName: string
		Currency: string
		RecommendedFrequency: Frequency
		OutstandingBalance: number
		Recommendations: Record<Frequency, {NumberOfPayments: number}>
	}
	enabled: boolean
	sliderValue: number
}) => {
	const result = useQuery({
		queryKey: ["setupPage", paymentArrangementID, frequency, repayments, format(startDate, "yyyy-MM-dd")],
		queryFn: () =>
			getSetupPage({
				paymentArrangementID,
				frequency,
				numberOfPayments: repayments,
				startDate,
			}),
		staleTime: 1000 * 60 * 15, // 15 minutes
		enabled,
	})

	let setupPage = result.data

	if ((repayments !== sliderValue || !result.data) && configuration) {
		setupPage = calculatePaymentArrangementSetup({
			configuration: configuration,
			OutstandingBalance: configuration.OutstandingBalance,
			frequency,
			numberOfPayments: sliderValue,
			startDate,
		})
	}

	result.data = setupPage

	return result
}

export const useSetupConfig = (paymentArrangementID: string) => {
	const result = useQuery({
		queryKey: ["setupConfig", paymentArrangementID],
		queryFn: () => paymentArrangementAPI.getSetupConfig(paymentArrangementID),
	})

	return result
}

export const useSetupMutation = ({
	paymentArrangementID,
	frequency,
	startDate,
	numberOfPayments,
}: {
	paymentArrangementID: string
	frequency: Frequency
	startDate: Date
	numberOfPayments: number
}) => {
	const navigate = useNavigate()
	const {notification} = useNotification()
	const {t} = useTranslation()
	const result = useMutation({
		mutationFn: () =>
			paymentArrangementAPI.postSetup({paymentArrangementID, frequency, startDate, numberOfPayments}),
		onSuccess: (response) => {
			navigate(nextPage(response.Next, paymentArrangementID))
		},
		onError: (error) => {
			console.error(error)
			notification.error(error.message ?? t("unexpected_error.message"))
		},
	})

	return result
}
